/**
 * Selfie Storage Utility
 * Required handoff retrieval with sessionStorage caching only.
 */

import { logger } from './logger';
import { storageService, StorageKeys } from '../services/StorageService';
import { bestSelfieService } from '../services/BestSelfieService';
import { runtimeConfig } from '../services/RuntimeConfigService';
import { getSessionState } from './SessionStateManager';

interface VerificationSelfieData {
  best_selfie: string;
  session_id: string;
  timestamp: number;
}

declare global {
  interface Window {
    verificationSelfieData?: VerificationSelfieData;
  }
}

const MAX_SELFIE_AGE = 60 * 60 * 1000; // 1 hour

export interface StoredVerificationData {
  best_selfie: string;
  session_id: string;
  timestamp: number;
}

/**
 * Cache best_selfie in sessionStorage for performance
 * This is only a same-browser cache; Portal API owns fresh retrieval.
 */
export function storeBestSelfie(
  best_selfie: string,
  session_id: string
): boolean {
  const data: StoredVerificationData = {
    best_selfie,
    session_id,
    timestamp: Date.now(),
  };

  const success = storageService.setItem(StorageKeys.SELFIE_DATA, data, {
    fallbackToMemory: true,
  });

  if (!success) {
    logger.warn('Failed to cache best_selfie in sessionStorage');
  }

  return success;
}

/**
 * Retrieve cached best_selfie from sessionStorage
 * This only returns cached data; use fetchBestSelfieHandoff for fresh data.
 */
export function getBestSelfie(): StoredVerificationData | null {
  logger.debug('getBestSelfie: Checking sessionStorage cache...');

  // Check sessionStorage cache only
  const data = storageService.getItem<StoredVerificationData>(
    StorageKeys.SELFIE_DATA,
    {
      maxAge: MAX_SELFIE_AGE,
      fallbackToMemory: true,
    }
  );

  if (data) {
    logger.debug('getBestSelfie: found cached data in sessionStorage', {
      labels: {
        hasSelfie: `${!!data.best_selfie}`,
        sessionId: data.session_id,
        timestamp: `${data.timestamp}`,
        age: `${Date.now() - data.timestamp}`,
      }
    });
    return data;
  }

  logger.debug('getBestSelfie: no cached data found');
  return null;
}

/**
 * Fetch the required best-selfie handoff from Portal API.
 */
export async function fetchBestSelfieHandoff(
  sessionId?: string
): Promise<StoredVerificationData | null> {
  if (!sessionId) {
    const sessionState = getSessionState();
    sessionId = sessionState?.sessionId;

    if (!sessionId) {
      logger.warn('Required selfie handoff has no session ID');
      return null;
    }
  }

  const config = await runtimeConfig.loadConfig();
  const apiUrl = config.apiUrl;

  if (!apiUrl) {
    logger.warn('Required selfie handoff has no API configuration');
    return null;
  }

  logger.info('Fetching required selfie handoff', {
    labels: { hasToken: `${!!config.sessionToken}` },
  });

  const bestSelfie = await bestSelfieService.fetchBestSelfie(
    sessionId,
    apiUrl,
    config.sessionToken || undefined
  );

  const data: StoredVerificationData = {
    best_selfie: bestSelfie,
    session_id: sessionId,
    timestamp: Date.now(),
  };

  storageService.setItem(StorageKeys.SELFIE_DATA, data, {
    fallbackToMemory: true,
  });

  return data;
}

/**
 * Clear stored selfie data (call after successful verification)
 */
export function clearBestSelfie(): void {
  storageService.removeItem(StorageKeys.SELFIE_DATA, {
    fallbackToMemory: true,
  });

  // Also clean up any legacy window data
  delete window.verificationSelfieData;
}

/**
 * Check if best_selfie is available
 */
export function hasBestSelfie(): boolean {
  return getBestSelfie() !== null;
}

/**
 * Get just the base64 selfie string
 */
export function getBestSelfieImage(): string | null {
  const data = getBestSelfie();
  return data?.best_selfie || null;
}

/**
 * Get just the session ID
 */
export function getVerificationSessionId(): string | null {
  const data = getBestSelfie();
  return data?.session_id || null;
}
